// CalibrationLogModal.js
// Shared modal for adding/editing calibration log sessions. Used by the Sampling Event form and the
// standalone Calibration Log Book. Uses a temporary state pattern - all edits are held in tempState
// until Save is clicked. Decoupled from any specific parent (uses getExistingLogById + meters props).

import React, { useState, useMemo, useRef } from "react";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { SamplingEventTimePickerInput } from "../../../samplingEventForm/common/SamplingEventTimePickerInput";
import { CalibrationParameterFields } from "./CalibrationParameterFields";
import { v4 as uuidv4 } from "uuid";
// Styles
import styles from "../CalibrationLog.module.css";

// Log Modal Component
export const CalibrationLogModal = ({
  onClose,
  onSave,
  editLogId,
  isAddMode,
  headerString,
  user,
  meters = [],
  getExistingLogById,
}) => {
  // Prevent double-saves - use ref to survive re-renders during async save
  const isSavingRef = useRef(false);

  // ============================================================================
  // TEMPORARY STATE - All modal data lives here until Save
  // ============================================================================
  const [tempState, setTempState] = useState(() => {
    const currentDate = new Date();
    const formattedDate = currentDate.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
    const formattedTime = currentDate.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    // If editing an existing log, populate from it
    if (!isAddMode && editLogId) {
      const existingLog = getExistingLogById
        ? getExistingLogById(editLogId)
        : null;

      if (existingLog) {
        return {
          sharedFields: {
            calibrationDate: existingLog.calibrationDate || formattedDate,
            calibrationTime: existingLog.calibrationTime || formattedTime,
            instrument: existingLog.instrument || "",
            employeeId: user?.employee_id || null,
            employeeName: user?.name || "",
          },
          logs: {
            [editLogId]: {
              fieldParameter: existingLog.fieldParameter || "",
              standardUsed: existingLog.standardUsed || "",
              verification: existingLog.verification || "",
              units: existingLog.units || "",
              passFail: existingLog.passFail || "",
              standardTop: null,
              standardBottom: null,
            },
          },
          existingLogIds: [editLogId],
        };
      }
    }

    // Adding new - create blank temp state with one new log entry
    const newTempId = uuidv4();
    return {
      sharedFields: {
        calibrationDate: formattedDate,
        calibrationTime: formattedTime,
        instrument: "",
        employeeId: user?.employee_id || null,
        employeeName: user?.name || "",
      },
      logs: {
        [newTempId]: {
          fieldParameter: "",
          standardUsed: "",
          verification: "",
          units: "",
          passFail: "",
          standardTop: null,
          standardBottom: null,
        },
      },
      existingLogIds: [],
    };
  });

  // Get the list of log IDs for rendering parameter fields
  const logIds = useMemo(() => Object.keys(tempState.logs), [tempState.logs]);

  // ============================================================================
  // SELECTED METER STATE (for calibration parameters dropdown options)
  // ============================================================================
  const [selectedMeter, setSelectedMeter] = useState(() => {
    const instrumentValue = tempState.sharedFields.instrument;
    if (!instrumentValue) return null;

    const meter = meters?.find(
      (m) => m.manufacturer + " " + m.model_number === instrumentValue
    );

    return meter
      ? {
          label: meter.manufacturer + " " + meter.model_number,
          value: meter.manufacturer + " " + meter.model_number,
          calibration_parameters: meter.calibration_parameters,
        }
      : null;
  });

  // ============================================================================
  // SHARED FIELD HANDLERS (update tempState.sharedFields)
  // ============================================================================
  const handleAutoFillDateTime = () => {
    const currentDate = new Date();
    const formattedTime = currentDate.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    const formattedDate = currentDate.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });

    setTempState((prev) => ({
      ...prev,
      sharedFields: {
        ...prev.sharedFields,
        calibrationDate: formattedDate,
        calibrationTime: formattedTime,
      },
    }));
  };

  const handleDateChange = (date) => {
    if (!date) return;
    const dateString = date.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });

    setTempState((prev) => ({
      ...prev,
      sharedFields: {
        ...prev.sharedFields,
        calibrationDate: dateString,
      },
    }));
  };

  const handleTimeChange = (time) => {
    setTempState((prev) => ({
      ...prev,
      sharedFields: {
        ...prev.sharedFields,
        calibrationTime: time,
      },
    }));
  };

  const handleInstrumentChange = (selected) => {
    if (selected?.value === selectedMeter?.value) {
      return;
    }

    // Update selected meter for calibration parameters
    setSelectedMeter(selected);

    // Update instrument in shared fields and reset all log parameter fields
    setTempState((prev) => {
      const updatedLogs = {};
      for (const logId of Object.keys(prev.logs)) {
        updatedLogs[logId] = {
          fieldParameter: "",
          standardUsed: "",
          verification: "",
          units: "",
          passFail: "",
          standardTop: null,
          standardBottom: null,
        };
      }

      return {
        ...prev,
        sharedFields: {
          ...prev.sharedFields,
          instrument: selected?.value || "",
        },
        logs: updatedLogs,
      };
    });
  };

  // ============================================================================
  // LOG-SPECIFIC FIELD HANDLERS (update tempState.logs[logId])
  // ============================================================================
  const updateTempLog = (logId, updates) => {
    setTempState((prev) => ({
      ...prev,
      logs: {
        ...prev.logs,
        [logId]: {
          ...prev.logs[logId],
          ...updates,
        },
      },
    }));
  };

  // ============================================================================
  // ADD/REMOVE PARAMETER ROW HANDLERS
  // ============================================================================
  const addBlankParamHandler = () => {
    const newTempId = uuidv4();

    setTempState((prev) => ({
      ...prev,
      logs: {
        ...prev.logs,
        [newTempId]: {
          fieldParameter: "",
          standardUsed: "",
          verification: "",
          units: "",
          passFail: "",
          standardTop: null,
          standardBottom: null,
        },
      },
    }));
  };

  const removeLogHandler = (logId) => {
    setTempState((prev) => {
      const { [logId]: removed, ...remainingLogs } = prev.logs;
      return {
        ...prev,
        logs: remainingLogs,
      };
    });
  };

  // ============================================================================
  // SAVE HANDLER - Commits tempState to parent
  // ============================================================================
  const handleSaveLogs = () => {
    // Prevent double-saves
    if (isSavingRef.current) {
      return;
    }
    isSavingRef.current = true;

    // Pass the entire tempState to the parent for committing to actual data
    onSave(tempState);
  };

  // ============================================================================
  // RENDER
  // ============================================================================
  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h3>{headerString}</h3>
          <button className={styles.closeButton} onClick={onClose}>
            &times;
          </button>
        </div>
        <div className={styles.modalBody}>
          {/* Date, Time, Name, Instrument Fields (Shared Fields) */}
          <div className={styles.modalRow}>
            <div className={styles.modalField}>
              <button
                className={styles.dateTimeAutoFillButton}
                type="button"
                onClick={handleAutoFillDateTime}
              >
                Auto-fill Date & Time
              </button>
            </div>
            <div className={styles.modalField}>
              <label className={styles.modalLabel}>Date</label>
              <div className={styles.modalDatePickerContainer}>
                <DatePicker
                  selected={
                    tempState.sharedFields.calibrationDate
                      ? new Date(tempState.sharedFields.calibrationDate)
                      : null
                  }
                  onChange={handleDateChange}
                  dateFormat="MM/dd/yyyy"
                  className={styles.modalInput}
                  placeholderText="Select date"
                  showMonthDropdown
                  showYearDropdown
                  scrollableYearDropdown
                  portalId="root"
                  popperProps={{
                    strategy: "fixed",
                    modifiers: [
                      {
                        name: "zIndex",
                        enabled: true,
                        options: { zIndex: 9999 },
                      },
                    ],
                  }}
                />
              </div>
            </div>
            <div className={styles.modalField}>
              <label className={styles.modalLabel}>Time</label>
              <div className={styles.modalDatePickerContainer}>
                <SamplingEventTimePickerInput
                  value={tempState.sharedFields.calibrationTime}
                  onChange={handleTimeChange}
                  className={styles.modalInput}
                />
              </div>
            </div>

            <div className={styles.modalField}>
              <label className={styles.modalLabel}>Name</label>
              <input
                type="text"
                className={styles.modalInput}
                value={user?.name || ""}
                placeholder="Enter your name"
                readOnly={true}
              />
            </div>
            <div className={styles.modalField}>
              <label className={styles.modalLabel}>Instrument</label>
              <div className={styles.modalSelectContainer}>
                <Select
                  options={meters.map((meter) => ({
                    label: meter.manufacturer + " " + meter.model_number,
                    value: meter.manufacturer + " " + meter.model_number,
                    calibration_parameters: meter.calibration_parameters,
                  }))}
                  value={
                    selectedMeter
                      ? {
                          label: selectedMeter.label,
                          value: selectedMeter.value,
                        }
                      : null
                  }
                  onChange={handleInstrumentChange}
                  placeholder="Select instrument"
                  isClearable
                />
              </div>
            </div>
          </div>

          <hr className={styles.modalSeparator} />
          <h5>Calibration Parameters</h5>

          {/* Render parameter fields from tempState */}
          {logIds.map((logId) => (
            <CalibrationParameterFields
              key={logId}
              logId={logId}
              tempLogData={tempState.logs[logId]}
              updateTempLog={updateTempLog}
              onRemoveLog={removeLogHandler}
              selectedMeter={selectedMeter}
              canRemove={logIds.length > 1}
            />
          ))}
        </div>
        <div className={styles.modalFooter}>
          <button
            className={styles.addParamButton}
            onClick={addBlankParamHandler}
          >
            Add Blank Parameter
          </button>
          <button className={styles.cancelButton} onClick={onClose}>
            Cancel
          </button>
          <button className={styles.saveButton} onClick={handleSaveLogs}>
            Save Log Session
          </button>
        </div>
      </div>
    </div>
  );
};
